"use client";

/**
 * Marketing nav for the public landing + marketing pages.
 *
 * Enterprise mega-menu navigation: five teaching dropdowns (Product,
 * Use-cases, Integrations, Security, About) plus two standalone links
 * (Pricing, Contact). Each panel answers "what can I do with this?" — every
 * item is an icon + title + one-sentence value line, sourced from
 * `menu-data.ts` and the `landingNav.mm` i18n namespace (EN + NL in
 * lockstep via the LocaleProvider in the root layout).
 *
 * Interaction model (APG disclosure-nav):
 * - Hover intent: a short open delay prevents flicker when the pointer
 *   crosses the bar; moving between triggers switches panels instantly.
 * - Click toggles; Escape closes and restores focus to the trigger;
 *   outside click closes.
 * - Arrow keys: ←/→ move across the top level, ↓ opens a panel and enters
 *   it, ↑/↓ walk the links inside a panel.
 * - Panels mount only while open (lazy render) and are absolutely
 *   positioned, so opening one never shifts layout.
 *
 * Mobile keeps the existing pattern: a full-width sheet with the same
 * groups flattened to plain link lists.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronDown, LayoutDashboard, Menu, X } from "lucide-react";
import { useAuth } from "../auth-provider";
import { BrandLogo } from "../brand";
import { LanguageSwitcher } from "../language-switcher";
import { useLocale, useT } from "@/i18n";
import { MegaMenu } from "./mega-menu";
import {
  IntegrationItem,
  MenuCard,
  MenuChecklist,
  MenuCta,
  MenuHeading,
} from "./menu-card";
import {
  ABOUT_ITEMS,
  INTEGRATION_COLUMNS,
  NAV_ENTRIES,
  PRODUCT_CARDS,
  PRODUCT_CTA,
  PRODUCT_WHY_KEYS,
  SECURITY_CARDS,
  SECURITY_CTA,
  USE_CASE_CARDS,
  resolveHref,
  type MenuCardItem,
  type MenuId,
} from "./menu-data";

const OPEN_DELAY_MS = 60;
const CLOSE_DELAY_MS = 120;

export function LandingNav() {
  const { user, loading } = useAuth();
  const authed = !loading && !!user;
  const t = useT();
  const pathname = usePathname();

  const [openMenu, setOpenMenu] = useState<MenuId | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRefs = useRef<Partial<Record<MenuId, HTMLButtonElement | null>>>({});

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  const close = useCallback(() => {
    clearTimers();
    setOpenMenu(null);
  }, [clearTimers]);

  // Hover intent: small open delay from a cold bar; instant switch while a
  // panel is already open; small close delay so trigger→panel travel and
  // brief pointer exits don't flicker.
  function onItemEnter(id: MenuId) {
    clearTimers();
    if (openMenu !== null) setOpenMenu(id);
    else openTimer.current = setTimeout(() => setOpenMenu(id), OPEN_DELAY_MS);
  }

  const onItemLeave = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => setOpenMenu(null), CLOSE_DELAY_MS);
  }, [clearTimers]);

  // Escape closes (restoring focus to the trigger); outside click closes.
  useEffect(() => {
    if (!openMenu) return;
    function onPointer(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) close();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        triggerRefs.current[openMenu as MenuId]?.focus();
        close();
      }
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [openMenu, close]);

  // The logo links to "/". When already on the homepage a <Link> to the
  // current route is a no-op, so scroll to the top instead.
  function onLogoClick(e: React.MouseEvent) {
    if (pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      setMobileOpen(false);
    }
  }

  // Arrow-key navigation. Top level: ←/→/Home/End move focus, ↓ opens and
  // enters a panel. Inside a panel: ↑/↓ walk the links (↑ from the first
  // link returns to the trigger).
  function onNavKeyDown(e: React.KeyboardEvent) {
    const target = e.target as HTMLElement;
    const topItems = Array.from(
      navRef.current?.querySelectorAll<HTMLElement>("[data-nav-top]") ?? [],
    );
    const topIndex = topItems.indexOf(target);

    if (topIndex !== -1) {
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        const next =
          topItems[(topIndex + (e.key === "ArrowRight" ? 1 : -1) + topItems.length) % topItems.length];
        next?.focus();
      } else if (e.key === "Home" || e.key === "End") {
        e.preventDefault();
        topItems[e.key === "Home" ? 0 : topItems.length - 1]?.focus();
      } else if (e.key === "ArrowDown") {
        const id = target.getAttribute("data-menu-id") as MenuId | null;
        if (id) {
          e.preventDefault();
          clearTimers();
          setOpenMenu(id);
          // Panel mounts on open — focus its first link on the next frame.
          requestAnimationFrame(() => {
            navRef.current
              ?.querySelector<HTMLElement>(`#mm-${id} a`)
              ?.focus();
          });
        }
      }
      return;
    }

    // Inside an open panel.
    const panel = target.closest<HTMLElement>("[id^='mm-']");
    if (!panel) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const links = Array.from(panel.querySelectorAll<HTMLElement>("a"));
      const i = links.indexOf(target);
      if (e.key === "ArrowDown") {
        links[Math.min(i + 1, links.length - 1)]?.focus();
      } else if (i <= 0) {
        triggerRefs.current[panel.id.slice(3) as MenuId]?.focus();
      } else {
        links[i - 1]?.focus();
      }
    }
  }

  const cta = authed ? (
    <Link
      href="/app"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-grad text-white text-[14px] font-medium shadow-glow hover:brightness-110 transition"
    >
      <LayoutDashboard size={15} />
      {t("landingNav.openApp")}
    </Link>
  ) : (
    <Link
      href="/signup"
      className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-lg bg-brand-grad text-white text-[13px] sm:text-[15px] font-medium whitespace-nowrap shadow-glow hover:brightness-110 transition"
    >
      {t("landingNav.tryFree")}
    </Link>
  );

  const closeAll = useCallback(() => {
    close();
    setMobileOpen(false);
  }, [close]);

  return (
    <header className="sticky top-0 z-40 border-b border-line-subtle bg-bg-base/80 backdrop-blur-md">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 h-20 flex items-center justify-between gap-3">
        <Link href="/" onClick={onLogoClick} className="flex items-center min-w-0">
          <span className="lg:hidden">
            <BrandLogo size={32} wordmarkClassName="text-[18px]" />
          </span>
          <span className="hidden lg:inline-flex">
            <BrandLogo size={42} wordmarkClassName="text-[24px]" />
          </span>
        </Link>

        {/* Desktop: five mega menus + two standalone links */}
        <nav ref={navRef} onKeyDown={onNavKeyDown} className="hidden lg:block">
          <ul className="flex items-center gap-0.5 text-[14.5px] text-fg-dim">
            {NAV_ENTRIES.map((entry) =>
              entry.kind === "link" ? (
                <li key={entry.id}>
                  <Link
                    href={entry.href}
                    data-nav-top
                    className="inline-flex whitespace-nowrap px-3 py-2 rounded-lg hover:text-fg-bright hover:bg-bg-raised/60 focus-visible:text-fg-bright transition"
                  >
                    {t(entry.labelKey)}
                  </Link>
                </li>
              ) : (
                <li
                  key={entry.id}
                  className="relative"
                  onMouseEnter={() => onItemEnter(entry.id)}
                  onMouseLeave={onItemLeave}
                >
                  <button
                    type="button"
                    ref={(el) => {
                      triggerRefs.current[entry.id] = el;
                    }}
                    data-nav-top
                    data-menu-id={entry.id}
                    aria-expanded={openMenu === entry.id}
                    aria-controls={`mm-${entry.id}`}
                    onClick={() => {
                      clearTimers();
                      setOpenMenu(openMenu === entry.id ? null : entry.id);
                    }}
                    className={`inline-flex items-center gap-1 whitespace-nowrap px-3 py-2 rounded-lg transition hover:bg-bg-raised/60 ${
                      openMenu === entry.id ? "text-fg-bright bg-bg-raised/60" : "hover:text-fg-bright"
                    }`}
                  >
                    {t(entry.labelKey)}
                    <ChevronDown
                      size={14}
                      aria-hidden
                      className={`transition-transform duration-150 ${openMenu === entry.id ? "rotate-180" : ""}`}
                    />
                  </button>

                  {openMenu === entry.id && (
                    <MenuPanel id={entry.id} onNavigate={closeAll} />
                  )}
                </li>
              ),
            )}
          </ul>
        </nav>

        <div className="flex items-center gap-2.5">
          <LanguageSwitcher variant="inline" className="hidden sm:inline-flex" />
          <span className="hidden lg:inline-flex">{cta}</span>
          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? t("landingNav.closeMenu") : t("landingNav.openMenu")}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line-subtle text-fg-dim hover:text-fg-bright transition"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu: CTA at top, then the same groups as flat link lists */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-line-subtle bg-bg-base/95 backdrop-blur-md">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 py-5 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <div onClick={() => setMobileOpen(false)}>{cta}</div>

            <MobileGroup title={t("landingNav.product")} items={PRODUCT_CARDS} onNavigate={closeAll} />
            <MobileGroup title={t("landingNav.useCases")} items={USE_CASE_CARDS} onNavigate={closeAll} />
            <MobileGroup
              title={t("landingNav.integrations")}
              items={INTEGRATION_COLUMNS.flatMap((c) => c.items)}
              onNavigate={closeAll}
            />
            <MobileGroup title={t("landingNav.safety")} items={SECURITY_CARDS} onNavigate={closeAll} />
            <MobileGroup title={t("landingNav.mm.about.label")} items={ABOUT_ITEMS} onNavigate={closeAll} />

            <div className="mt-5 flex flex-col gap-1 border-t border-line-subtle pt-4">
              <Link href="/pricing" onClick={closeAll} className="py-2 text-[15px] text-fg-bright">
                {t("landingNav.pricing")}
              </Link>
              <Link href="/contact" onClick={closeAll} className="py-2 text-[15px] text-fg-bright">
                {t("landingNav.contact")}
              </Link>
            </div>

            <div className="mt-4 border-t border-line-subtle pt-4">
              <LanguageSwitcher variant="inline" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/* ── Panels ──────────────────────────────────────────────────────────── */

function MenuPanel({ id, onNavigate }: { id: MenuId; onNavigate: () => void }) {
  const t = useT();

  if (id === "product") {
    return (
      <MegaMenu id="mm-product" align="left" width={640}>
        <div className="grid grid-cols-[1.35fr_1fr] gap-3">
          <div className="p-1">
            <div className="px-3 pt-1.5">
              <MenuHeading>{t("landingNav.mm.product.eyebrow")}</MenuHeading>
              <p className="mt-1.5 text-[12.5px] leading-snug text-fg-muted">
                {t("landingNav.mm.product.tagline")}
              </p>
            </div>
            <div className="mt-2 flex flex-col gap-0.5">
              {PRODUCT_CARDS.map((item) => (
                <MenuCard key={item.key} item={item} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
          <div className="flex flex-col rounded-xl border border-line-subtle bg-bg-base/50 p-4">
            <MenuHeading>{t("landingNav.mm.product.whyTitle")}</MenuHeading>
            <div className="mt-3.5 flex-1">
              <MenuChecklist keys={PRODUCT_WHY_KEYS} prefix="product.why" />
            </div>
            <div className="mt-4 border-t border-line-subtle pt-3.5">
              <MenuCta labelKey={PRODUCT_CTA.key} href={PRODUCT_CTA.href} onNavigate={onNavigate} />
            </div>
          </div>
        </div>
      </MegaMenu>
    );
  }

  if (id === "use-cases") {
    return (
      <MegaMenu id="mm-use-cases" align="left" width={580}>
        <div className="grid grid-cols-2 gap-0.5 p-1">
          {USE_CASE_CARDS.map((item) => (
            <MenuCard key={item.key} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      </MegaMenu>
    );
  }

  if (id === "integrations") {
    return (
      <MegaMenu id="mm-integrations" align="center" width={880}>
        <div className="grid grid-cols-4 gap-2 p-1">
          {INTEGRATION_COLUMNS.map((col) => (
            <div key={col.key}>
              <div className="px-2.5 pt-1.5 pb-1">
                <MenuHeading>{t(`landingNav.mm.integrations.${col.key}`)}</MenuHeading>
              </div>
              <div className="flex flex-col gap-0.5">
                {col.items.map((item) => (
                  <IntegrationItem key={item.key} item={item} onNavigate={onNavigate} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </MegaMenu>
    );
  }

  if (id === "security") {
    return (
      <MegaMenu id="mm-security" align="center" width={560}>
        <div className="grid grid-cols-2 gap-0.5 p-1">
          {SECURITY_CARDS.map((item) => (
            <MenuCard key={item.key} item={item} onNavigate={onNavigate} />
          ))}
        </div>
        <div className="mx-1 mt-1.5 border-t border-line-subtle px-3 pb-1 pt-3">
          <MenuCta labelKey={SECURITY_CTA.key} href={SECURITY_CTA.href} onNavigate={onNavigate} />
        </div>
      </MegaMenu>
    );
  }

  // About — deliberately small.
  return (
    <MegaMenu id="mm-about" align="right" width={300}>
      <div className="flex flex-col gap-0.5 p-1">
        {ABOUT_ITEMS.map((item) => (
          <MenuCard key={item.key} item={item} onNavigate={onNavigate} />
        ))}
      </div>
    </MegaMenu>
  );
}

/* ── Mobile ──────────────────────────────────────────────────────────── */

function MobileGroup({
  title,
  items,
  onNavigate,
}: {
  title: string;
  items: MenuCardItem[];
  onNavigate: () => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  return (
    <div className="mt-5 border-t border-line-subtle pt-4">
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
        {title}
      </div>
      <div className="mt-2 flex flex-col">
        {items.map((item) => (
          <Link
            key={item.key}
            href={resolveHref(item.href, locale)}
            onClick={onNavigate}
            className="py-2 text-[15px] text-fg-dim hover:text-fg-bright transition"
          >
            {t(`landingNav.mm.${item.key}.title`)}
          </Link>
        ))}
      </div>
    </div>
  );
}
